'use client'

import type { ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import { Search, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './Button'

// ============================================================================
// ПУСТОЙ СПИСОК — ОДНА ЗАГЛУШКА ВО ВСЕХ РАЗДЕЛАХ (nRgaLPz1, макет 26.10).
// ----------------------------------------------------------------------------
// Иконка, заголовок, строка подсказки, одна кнопка:
// - `filtered` — поиск или фильтры ничего не нашли: «Нет … по выбранным
//   фильтрам» и «Сбросить поиск и фильтры» (если раздел передал `onReset`);
// - `none` — записей ещё нет: «Пока нет …» и кнопка создания, если раздел её
//   передал (`action`).
//
// `frame="card"` — карточкой (телефон). `frame="table"` — блок в теле
// таблицы. Таблицы шире окна прокручиваются вбок вместе с шапкой, и блок
// по центру ширины ВСЕХ колонок уезжал за правый край (на 768–1100 px
// «Показать» было не достать). Поэтому блок прилипает к левому краю
// прокрутки и берёт ширину ВИДИМОЙ части: у прокрутки `LIST_EMPTY_SCROLLER`
// (container-type), у блока — 100cqw.
// ============================================================================

/** Класс для прокручиваемой обёртки таблицы, внутри которой стоит `<ListEmpty frame="table">`. */
export const LIST_EMPTY_SCROLLER = '[container-type:inline-size]'

interface ListEmptyProps {
  /** Иконка раздела — для `none`. У `filtered` иконка одна на всех: лупа. */
  icon: LucideIcon
  title: string
  hint?: ReactNode
  /** Дополнительные строки под подсказкой (например «Найдено в …»). */
  children?: ReactNode
  variant: 'none' | 'filtered'
  /** `filtered`: сброс поиска и фильтров. Без него кнопки нет. */
  onReset?: () => void
  /** `none`: кнопка создания — только там, где у раздела есть право создавать. */
  action?: ReactNode
  frame?: 'card' | 'table'
  className?: string
}

export function ListEmpty({
  icon: Icon, title, hint, children, variant, onReset, action, frame = 'table', className,
}: ListEmptyProps) {
  const t = useTranslations()
  const Shown = variant === 'filtered' ? Search : Icon
  const button = variant === 'filtered'
    ? (onReset && (
        <Button type="button" variant="secondary" size="sm" onClick={onReset} className="min-h-[44px] md:min-h-0">
          {t('table.empty.reset')}
        </Button>
      ))
    : action
  return (
    <div
      data-list-empty={variant}
      className={cn(
        'flex flex-col items-center gap-2 text-center',
        frame === 'card'
          ? 'rounded-lg border-hairline border-ink-200 bg-white px-4 py-10'
          : 'sticky left-0 w-[100cqw] max-w-full px-6 py-16',
        className,
      )}
    >
      <Shown size={28} strokeWidth={1.5} className="mb-1 text-ink-300" aria-hidden="true" />
      <p className="text-sm font-medium text-ink-700">{title}</p>
      {hint && <p className="max-w-md text-[13px] text-ink-500">{hint}</p>}
      {children}
      {button && <div className="mt-2">{button}</div>}
    </div>
  )
}
